// ABOUTME: Firestore access for the sponsored-link platform (`ads` collection).
// ABOUTME: Reads normalize legacy documents; writes validate and server-stamp.

import { db } from "./firebase";
import {
  collection,
  addDoc,
  doc,
  deleteDoc,
  onSnapshot,
  query,
  orderBy,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import {
  normalizeAdRecord,
  prepareAdInput,
  validateAdInput,
  type AdInput,
  type AdRecord,
} from "../utils/ads";

// Re-exported so existing imports (`AdData`) keep working.
export type { AdRecord, AdInput } from "../utils/ads";
export type AdData = AdRecord;

const adsRef = () => collection(db, "ads");

export class AdValidationError extends Error {
  constructor(public readonly fields: ReturnType<typeof validateAdInput>) {
    super("Sponsored link has invalid fields: " + Object.keys(fields).join(", "));
    this.name = "AdValidationError";
  }
}

function assertValid(input: AdInput): AdInput {
  const prepared = prepareAdInput(input);
  const errors = validateAdInput(prepared);
  if (Object.keys(errors).length > 0) throw new AdValidationError(errors);
  return prepared;
}

export const adService = {
  /**
   * Live subscription to every ad, live or not, ordered by title. The
   * dashboard filters to live ads itself (see `pickWeightedAd`) so the admin
   * list and the rotation read the same snapshot.
   */
  subscribeToAds(callback: (ads: AdRecord[]) => void, onError?: (err: Error) => void) {
    const q = query(adsRef(), orderBy("title"));
    return onSnapshot(
      q,
      (snap) => {
        const ads: AdRecord[] = [];
        snap.forEach((docSnap) => ads.push(normalizeAdRecord(docSnap.id, docSnap.data())));
        callback(ads);
      },
      (err) => {
        console.warn("Ad subscription failed:", err);
        onError?.(err);
      }
    );
  },

  /** Create an ad. Throws `AdValidationError` before touching Firestore if the input is bad. */
  async addAd(input: AdInput): Promise<void> {
    const prepared = assertValid(input);
    await addDoc(adsRef(), {
      ...prepared,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  },

  /**
   * Replace an ad's editable fields. Same validation as `addAd`. This is a
   * full-document write, not a merge: pre-revamp documents may lack fields
   * (or carry stray ones) that the Firestore rule now requires, and a merge
   * would leave them in a shape the rule rejects.
   */
  async updateAd(existing: AdRecord, input: AdInput): Promise<void> {
    const prepared = assertValid(input);
    await setDoc(doc(adsRef(), existing.id), {
      ...prepared,
      createdAt: existing.createdAt ?? serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  },

  /** Flip only the active flag — the one-click pause/resume in the admin list. */
  async setAdActive(existing: AdRecord, active: boolean): Promise<void> {
    await this.updateAd(existing, { ...existing, active });
  },

  async deleteAd(id: string): Promise<void> {
    await deleteDoc(doc(adsRef(), id));
  },
};
